import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { guestWL, GUEST_LIMITS } from "@/lib/guestWatchlists";

// Unified watchlist API: talks to the backend when signed in, else localStorage (guest demo).
export function useWatchlists() {
  const { user } = useAuth();
  const isGuest = user === false;
  const ready = user !== null; // null = auth still resolving
  const [lists, setLists] = useState([]);
  const [limits, setLimits] = useState(GUEST_LIMITS);

  const reload = useCallback(async () => {
    if (user) {
      const { data } = await api.get("/watchlists");
      setLists(data.lists);
      setLimits(data.limits);
    } else if (isGuest) {
      setLists(guestWL.get().lists);
      setLimits(GUEST_LIMITS);
    }
  }, [user, isGuest]);

  useEffect(() => { if (ready) reload(); }, [ready, reload]);

  useEffect(() => {
    if (!isGuest) return;
    const h = () => setLists(guestWL.get().lists);
    window.addEventListener("sl-guest-wl", h);
    return () => window.removeEventListener("sl-guest-wl", h);
  }, [isGuest]);

  const createList = useCallback(async (name) => {
    if (user) { await api.post("/watchlists", { name }); await reload(); }
    else { guestWL.createList(name); }
  }, [user, reload]);

  const renameList = useCallback(async (id, name) => {
    if (user) { await api.patch(`/watchlists/${id}`, { name }); await reload(); }
    else { guestWL.renameList(id, name); }
  }, [user, reload]);

  const deleteList = useCallback(async (id) => {
    if (user) { await api.delete(`/watchlists/${id}`); await reload(); }
    else { guestWL.deleteList(id); }
  }, [user, reload]);

  const addPlayer = useCallback(async (listId, player) => {
    if (user) { await api.post(`/watchlists/${listId}/players`, { player_id: player.id }); await reload(); }
    else { guestWL.addPlayer(listId, player); }
  }, [user, reload]);

  const removePlayer = useCallback(async (listId, playerId) => {
    if (user) { await api.delete(`/watchlists/${listId}/players/${playerId}`); await reload(); }
    else { guestWL.removePlayer(listId, playerId); }
  }, [user, reload]);

  const toggleWatch = useCallback(async (player) => {
    const watched = lists.some((l) => l.players.some((p) => p.id === player.id));
    if (user) {
      if (watched) await api.delete(`/watchlist/${player.id}`);
      else await api.post("/watchlist", { player_id: player.id });
      await reload();
    } else {
      guestWL.toggleDefault(player);
    }
    return !watched;
  }, [user, lists, reload]);

  const isWatched = useCallback((pid) => lists.some((l) => l.players.some((p) => p.id === pid)), [lists]);
  const isInList = useCallback((listId, pid) =>
    !!lists.find((l) => l.id === listId)?.players.some((p) => p.id === pid), [lists]);

  return { lists, limits, isGuest, ready, reload,
           createList, renameList, deleteList, addPlayer, removePlayer, toggleWatch, isWatched, isInList };
}
